import { useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { interiorFloor } from '../state/interiorFloor';
import { floorBridge } from '../state/floorBridge';
import { buildBVH } from '../utils/bvh';
import { roomBody, roomWalker } from '../state/roomBody';
import { cutsceneBridge } from '../city/cutscene';

// FloorClamp — you can stand where there is floor, and nowhere else.
//
// Every bounds mechanism before this asked someone to DESCRIBE the walkable
// area: a box, then a box with a hole, then two radii. Each described a shape
// the room might have, and the underground has none of them — it is a ring that
// is not a circle, with openings, in a model nobody authored to a grid. The
// descriptions kept losing to the geometry.
//
// So this stops describing and starts asking. One ray, straight down, from where
// you are about to be. If it lands on the room, that is floor and you may stand
// there. If it hits nothing you have walked off the edge, and you are put back
// where you last stood. The walkable area is then EXACTLY the floor's real
// outline — ring, L-shape, platform with a pit in it, whatever the model is —
// with nothing measured and nothing to re-measure when the model changes.
//
// The same hit gives the floor's HEIGHT, which is how you walk down stairs
// instead of through them. Both fall out of one raycast per frame.
//
// Not a physics engine and not a substitute for one: it says nothing about
// walls, and you can still walk through a pillar. It solves the fall-off-the-
// world half, which is the half that was blocking.
const DOWN = new THREE.Vector3(0, -1, 0);
// THE RAY STARTS ABOVE YOUR FEET, NOT ABOVE YOUR HEAD.
//
// It used to start at eye + 1.2u, which is 1.95u off the floor — ABOVE THE
// CEILING of most rooms in this world. A downward ray from up there misses the
// room entirely and hits the ceiling slab's TOP face, which is a perfectly good
// horizontal surface as far as this code could tell, so it stood you on the
// roof. The salon's ceiling is at 1.55u and the metro's at ~1.29u; the corner
// market's happened to be 2.0u, which is the only reason this ever worked
// anywhere.
//
// A floor probe only ever needs to reach as high as the tallest thing you are
// allowed to STEP onto. Measuring from the feet makes that explicit and puts the
// origin permanently below any ceiling — there is no room this can outgrow,
// because it no longer scales with anything but the step height.
const STEP = 0.35;  // ≈0.77m — higher than any stair riser, lower than any ceiling
const REACH = 6;    // how far below to look before calling it a void
// ── A LOFT HAS AN EDGE (2026-10-30, The Jahva House) ─────────────────────────
// Khari: "near the back (upstairs preferably) is a small listening area". The
// first room with an upstairs. In a room flagged `multiLevel` (rooms.js) the
// walk no longer pins your eye to the height you arrived at, so this probe is
// what carries you up the stairs — and the same probe, looking down past the
// mezzanine's edge, sees the café floor 2.9m below: a hit, not a void. Followed
// like any other hit, it would drop you off the balcony in one frame. Railings
// cannot stop that: nothing in a room is a wall to this code (see the top).
//
// So a drop deeper than LEDGE is refused, the way "no floor" is — you are put
// back where you last stood. 0.8u ≈ 1.76m: an order of magnitude past a stair
// riser (0.08u) and short of a mezzanine (1.32u), and deep enough that anything
// you could have climbed onto on the ground floor is somewhere you can still
// step down from. ONLY in multiLevel rooms, and only once you have stood
// somewhere (`last`), so arriving, standing up and leaving freecam still land
// on whatever is below, however far.
const LEDGE = 0.8;

export default function FloorClamp({ eye = 0.75 }) {
  const camera = useThree((s) => s.camera);
  const interior = useWorldStore((s) => s.interior);
  const enabled = useWorldStore((s) => s.boundsEnabled);
  // Sitting is the one time the floor is NOT what sets your height. Without
  // this, the clamp puts your eye back at standing height every frame and the
  // chair appears to reject you.
  const seated = useWorldStore((s) => s.seated);
  // Freecam owns the height while it is on — see the store. Standing down here
  // rather than being overridden downstream keeps it to ONE thing setting y at
  // a time, which is the failure this room has already had twice.
  const freeCam = useWorldStore((s) => s.freeCam);
  // AND NOT WHILE THE RIG IS STILL FLYING YOU IN. Until camSettled, CameraRig
  // owns the camera outright; probing the floor under a position it is animating
  // through means both of them write it every frame, and the result is the entry
  // jitter. Same rule as freecam and seating — whoever owns the camera owns it
  // alone. This mattered even after walk-in rooms stopped easing, because the
  // orbit rooms still do.
  const camSettled = useWorldStore((s) => s.camSettled);
  const ray = useRef(new THREE.Raycaster());
  const last = useRef(null);
  const origin = useRef(new THREE.Vector3());
  const bounds = useRef(null); // the room's box, measured once per room
  // SAY WHEN YOU ARE HOLDING SOMEBODY STILL.
  //
  // The restore below is indistinguishable, from inside the world, from movement
  // being broken: the camera still turns, WASD still fires, and the position is
  // put back every frame. It reads as "I can rotate but I cannot walk", which
  // sends you looking at the controls — the one place the fault is not.
  //
  // Logged on the TRANSITION, not the state, or a room you cannot stand in
  // produces sixty lines a second and the console becomes useless exactly when
  // it is needed. The numbers are the ones that decide it: where the ray started,
  // how far it reached, and the room's own vertical extent.
  const missing = useRef(false);
  // ── ONE MISSED RAY IS NOT A CLIFF ────────────────────────────────────────
  //
  // Restoring the position on a single miss makes every flaky frame UNDO that
  // frame's movement, so you travel at exactly the probe's hit rate: land on a
  // surface the ray catches half the time — a train floor with seams, a grating,
  // coplanar panels — and you walk at half speed with no other symptom. It reads
  // as the room being sticky, not as a raycast failing, which is why it took
  // walking through a train to notice it.
  //
  // A real edge is not intermittent: step off and every subsequent ray misses.
  // So requiring consecutive misses separates a genuine drop from a flaky hit
  // while costing at most GRACE frames of overhang — 3 frames at 2.4 u/s is
  // 12cm, far less than the step you would take anyway.
  const misses = useRef(0);
  const GRACE = 3;
  // Hit/miss over a rolling window, so a patchy floor can be reported as a rate
  // rather than as a wall of per-frame warnings.
  const flaky = useRef({ hit: 0, miss: 0 });
  // Said once per approach to an edge, not once a frame — same rule as `missing`.
  const ledged = useRef(false);
  const mode = useRef(false);

  useFrame(() => {
    const root = interiorFloor.object;
    // `last` is cleared alongside, or leaving freecam would snap you back to the
    // last solid ground you stood on — undoing the move you just made by hand,
    // which is the whole point of having flown up there.
    if (!interior || !enabled || !root || seated || freeCam || !camSettled || cutsceneBridge.active) {   // …or a scene has the camera (RoomGreeting)
      // Counters reset too, or the grace window carries a stale miss streak into
      // the next room and drops you on the first frame back on the floor.
      last.current = null; missing.current = false; misses.current = 0;
      return;
    }

    // THE POINT THAT WALKS (state/roomBody): the camera in first person, the
    // body in third. A switch between them starts the floor history afresh, or
    // the first frame would snap one back to where the other last stood.
    const P = roomWalker(camera);
    if (mode.current !== roomBody.on) { mode.current = roomBody.on; last.current = null; }

    // Where the feet are, plus one step. `eye` is what turns the camera height
    // into a foot height, and it is the same constant the spawn was authored
    // against — so this tracks the room rather than a guess about it.
    const feet = P.y - eye;

    // AND NEVER ABOVE THE ROOM. Standing on the roof was self-sustaining: the
    // probe started above the roof, found the roof, and put you back on it every
    // frame with no way down. Capping the origin inside the model's own box
    // breaks that loop — from inside the ceiling slab a downward ray sees only
    // its underside, which is a back face and therefore skipped, so the probe
    // carries on to the real floor and drops you into the room.
    if (bounds.current?.src !== root) {
      bounds.current = { src: root, box: new THREE.Box3().setFromObject(root) };
      // Built on the same room-change as the box, so both are derived from the
      // model exactly once and neither can be stale relative to the other.
      const { built, tris } = buildBVH(root);
      if (built) console.log(`[floor] BVH built for ${built} mesh(es), ${tris.toLocaleString()} tris — the floor probe no longer scans them linearly`);
    }
    const ceiling = bounds.current.box.max.y - 0.01;
    origin.current.set(P.x, Math.min(feet + STEP, ceiling), P.z);
    ray.current.set(origin.current, DOWN);
    ray.current.far = STEP + REACH;
    // We want the floor, which is the FIRST thing under you — not a sorted list
    // of everything down to the tunnel. With a BVH this lets whole branches be
    // skipped once a nearer hit exists.
    ray.current.firstHitOnly = true;
    const hit = ray.current.intersectObject(root, true)[0];

    if (!hit) {
      misses.current += 1;
      floorBridge.miss += 1;
      // Under the grace window: hold your ground and your height, but do NOT
      // rewind. Rewinding is what turned a flaky probe into a speed limit.
      if (misses.current < GRACE) return;
      if (!missing.current) {
        missing.current = true;
        const b = bounds.current.box;
        console.warn(`[floor] NO FLOOR under ${P.x.toFixed(2)}, ${P.y.toFixed(2)}, ${P.z.toFixed(2)}`
          + ` in "${interior}" — holding you still (you can look, you cannot walk).`
          + `\n  ray from y ${origin.current.y.toFixed(2)} down ${(STEP + REACH).toFixed(2)} · room box y ${b.min.y.toFixed(2)}..${b.max.y.toFixed(2)}`
          + `\n  if the room LOOKS fine, the floor is single-sided facing DOWN — a raycast skips back faces, so it is invisible to this and solid to the eye.`);
      }
      // OFF THE EDGE. Restored rather than clamped to a boundary, because there
      // is no boundary to clamp to — the last place you stood is the only point
      // known to be solid, and it is by definition one step away.
      if (last.current) P.copy(last.current);
      return;
    }
    floorBridge.hit += 1;
    if (missing.current) {
      missing.current = false;
      console.log(`[floor] floor found again at y ${hit.point.y.toFixed(2)} — walking restored.`);
    }
    // A SURFACE THE PROBE KEEPS LOSING is worth saying out loud even though the
    // grace window now absorbs it. It is invisible once handled — the room just
    // feels slightly wrong — and it points at real geometry to fix: a floor with
    // seams, a back-facing panel, two coplanar surfaces flickering against each
    // other. Reported as a RATE, because one miss in a thousand is noise and one
    // in three is a floor that needs looking at.
    if (misses.current) {
      flaky.current.miss += misses.current;
      misses.current = 0;
    }
    flaky.current.hit += 1;
    if (flaky.current.hit + flaky.current.miss >= 600) {
      const rate = flaky.current.miss / (flaky.current.hit + flaky.current.miss);
      if (rate > 0.05) {
        console.warn(`[floor] the probe is missing ${(rate * 100).toFixed(0)}% of frames around `
          + `${P.x.toFixed(1)}, ${P.z.toFixed(1)} in "${interior}" — `
          + 'the floor there is patchy (seams, a back-facing panel, or two coplanar surfaces). '
          + 'Walking still works, but it used to be this exact rate as a SPEED PENALTY.');
      }
      flaky.current = { hit: 0, miss: 0 };
    }

    // THE LEDGE (see LEDGE above). Read from ROOMS every frame rather than
    // captured: registry venues are written into ROOMS at runtime.
    if (last.current && feet - hit.point.y > LEDGE && ROOMS[interior]?.multiLevel) {
      if (!ledged.current) {
        ledged.current = true;
        console.log(`[floor] ledge in "${interior}": ${(feet - hit.point.y).toFixed(2)}u drop at `
          + `${P.x.toFixed(2)}, ${P.z.toFixed(2)} — holding you at the edge.`);
      }
      P.copy(last.current);
      return;
    }
    ledged.current = false;

    // GROUND-FOLLOWING, which is the same measurement. Standing on the floor
    // rather than at a fixed height is what makes a staircase walkable, and it
    // is why the metro's two levels stop needing a hand-set offset.
    P.y = hit.point.y + eye;
    (last.current ||= new THREE.Vector3()).copy(P);
  });

  return null;
}
